import {Blankslate} from '@primer/react/experimental'
import {ListUnorderedIcon, ServerIcon, type Icon} from '@primer/octicons-react'

import styles from './SessionLogsBlankSlate.module.css'

/**
 * Why a session's events can't be shown right now. Each reason maps to its own
 * blank-slate copy so the placeholder explains the specific situation.
 *
 * - `no-events`: the session genuinely has no activity to show (non-AHP, or a
 *   completed session with nothing renderable).
 * - `ahp-offline`: an AHP session whose environment is offline. Historical
 *   events aren't restorable yet (that work is in progress).
 */
export type SessionLogsEmptyReason = 'no-events' | 'ahp-offline'

interface BlankSlateCopy {
  icon: Icon
  heading: string
  description: string
}

const COPY: Record<SessionLogsEmptyReason, BlankSlateCopy> = {
  'no-events': {
    icon: ListUnorderedIcon,
    heading: 'No events to display',
    description: 'There’s no activity to show for this session.',
  },
  'ahp-offline': {
    icon: ServerIcon,
    heading: 'Environment is offline',
    description: 'This session’s environment is offline. Its history will be available in an upcoming release.',
  },
}

export interface SessionLogsBlankSlateProps {
  reason: SessionLogsEmptyReason
}

/**
 * Placeholder shown in place of a session's event timeline when there is nothing
 * to render. Purely informational — there is no immediate action to take, so it
 * intentionally has no primary or secondary action.
 */
export function SessionLogsBlankSlate({reason}: SessionLogsBlankSlateProps) {
  const {icon: VisualIcon, heading, description} = COPY[reason]
  return (
    <div className={styles.container}>
      <Blankslate narrow>
        <Blankslate.Visual>
          <VisualIcon size={24} className={styles.visual} />
        </Blankslate.Visual>
        <Blankslate.Heading>{heading}</Blankslate.Heading>
        <Blankslate.Description>{description}</Blankslate.Description>
      </Blankslate>
    </div>
  )
}
